// Settings > Eklenti: organization-wide Outlook add-in switches
// (backend: signatures/views.py::addin_settings). Each toggle saves at once.
import React, { useEffect, useState } from 'react';
import { api, endpoints } from '../config/api';
import { Card, Alert, Toggle } from './ui';
import { ReactComponent as SettingsIcon } from '../icons/settings.svg';
import { useT } from '../i18n';
import './AddinBehaviorCard.css';

const messages = {
  title: { tr: 'Eklenti Davranışı', en: 'Add-in Behaviour' },
  inlineTitle: { tr: 'İmza görsellerini maile göm', en: 'Embed signature images in the mail' },
  inlineHint: {
    tr: 'Görseller maile ek olarak gömülür: mail açılır açılmaz görünürler ve alıcıda "resimleri indir" uyarısı çıkmaz. Kapalıyken görseller sunucumuzdan link olarak yüklenir.',
    en: 'Images go inside the mail as attachments: they show as soon as the mail opens, with no “download pictures” prompt for recipients. When off, they load from our server as links.',
  },
  enforceTitle: { tr: 'İmzamız her zaman en altta olsun', en: 'Always keep our signature at the bottom' },
  enforceHint: {
    tr: 'Kullanıcı Outlook\'un İmza düğmesiyle başka bir imza ya da şablon (örneğin fiyat listesi tablosu) eklediğinde Outlook bizim imzamızı siler. Bu ayar açıkken gönderim sırasında imzamız kontrol edilir; yoksa kullanıcının eklediği içeriğin altına yeniden eklenir. Klasik Outlook\'ta gönderim çok kısa bir süre uzayabilir.',
    en: 'When a user picks another signature or template with Outlook’s Signature button (e.g. a price-list table), Outlook removes ours. With this on, our signature is checked on send and, if missing, added back below whatever the user inserted. Sending in classic Outlook may take a moment longer.',
  },
  protectTitle: { tr: 'İmza değiştirilemesin', en: 'Signature can’t be edited' },
  protectHint: {
    tr: 'Gönderim sırasında imzadaki kişi bilgileri (ad, unvan, telefon, e-posta gibi şablon alanları) kontrol edilir. Kullanıcı bunlardan birini silmiş ya da değiştirmişse doğru imza geri konur; imzanın içine başka bir şey de yazılmışsa içerik kaybolmasın diye gönderim kısa bir uyarıyla durdurulur. Şablondaki serbest metinler (selamlama vb.) serbest kalır. Eklenti 1.0.40 ve sonrası.',
    en: 'On send, the sender’s details in the signature (template fields such as name, title, phone, e-mail) are checked. If one was deleted or changed, the correct signature is put back; if something else was also written inside the signature, sending is stopped with a short message so nothing is lost. Free text in the template (greetings etc.) stays editable. Add-in 1.0.40 and later.',
  },
  takesEffect: {
    tr: 'Değişiklikler kullanıcının açtığı bir sonraki mailde geçerli olur (eklenti 1.0.38 ve sonrası).',
    en: 'Changes apply from the next mail a user opens (add-in 1.0.38 and later).',
  },
  loadError: { tr: 'Eklenti ayarları yüklenemedi.', en: 'Couldn’t load add-in settings.' },
  saveError: { tr: 'Ayar kaydedilemedi, tekrar deneyin.', en: 'Couldn’t save the setting, try again.' },
};

const SWITCHES = [
  ['inline_signature_images', 'inlineTitle', 'inlineHint'],
  ['enforce_signature_on_send', 'enforceTitle', 'enforceHint'],
  ['protect_signature_on_send', 'protectTitle', 'protectHint'],
];

export default function AddinBehaviorCard() {
  const { t } = useT(messages);
  const [values, setValues] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState('');

  useEffect(() => {
    let cancelled = false;
    api.get(endpoints.signatures.addinSettings)
      .then((res) => { if (!cancelled) setValues(res); })
      .catch(() => { if (!cancelled) setError(t('loadError')); });
    return () => { cancelled = true; };
  }, [t]);

  const change = async (field, next) => {
    setSaving(field);
    setError('');
    const previous = values;
    setValues({ ...values, [field]: next });
    try {
      setValues(await api.put(endpoints.signatures.addinSettings, { [field]: next }));
    } catch (err) {
      setValues(previous);
      setError(t('saveError'));
    } finally {
      setSaving('');
    }
  };

  return (
    <Card className="settings-card">
      <div className="settings-card-header">
        <SettingsIcon className="section-icon" />
        <h2>{t('title')}</h2>
      </div>
      {error && <Alert variant="error">{error}</Alert>}
      {values && (
        <div className="addin-behavior-list">
          {SWITCHES.map(([field, title, hint]) => (
            <div className="addin-behavior-row" key={field}>
              <div className="addin-behavior-text">
                <div className="addin-behavior-title">{t(title)}</div>
                <p className="addin-behavior-hint">{t(hint)}</p>
              </div>
              <Toggle checked={!!values[field]} disabled={saving === field}
                onChange={(next) => change(field, next)} aria-label={t(title)} />
            </div>
          ))}
          <p className="field-hint-standalone">{t('takesEffect')}</p>
        </div>
      )}
    </Card>
  );
}
